import * as Sentry from '@sentry/nextjs';
import Button from 'components/buttons/button';
import CTA from 'components/Campaign/CTA';
import EmptyResults from 'components/Campaign/EmptyResults';
import { Letter } from 'components/cards/letter';
import { LoadingLetter, MoreLettersPill } from 'components/cards/loading';
import HexMap from 'components/HexMap';
import Anchor from 'components/typography/anchor';
import useScroll from 'hooks/useScroll';
import mastodon from 'images/icons/mastodon.svg';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import * as ReactUseWebSocket from 'react-use-websocket';
import config, { DATA_TYPES } from 'siteconfig';
import { useIntersection } from 'use-intersection';
import { themisMessageReceiptEventToLetter } from 'utilities/letters';
import { OpenLetterTypeList } from 'utilities/propTypes';
import { getStateProperName } from 'utilities/states';

// `react-use-websocket` is CJS-only and re-exports its default via a getter,
// which breaks under some bundlers' CJS/ESM interop. Depending on the
// bundler, the hook arrives as the namespace itself, at `.default`, or at
// `.default.default` (Rolldown in Storybook's production build wraps the whole
// `exports` object as the default). Unwrap until we reach the function.
// See: https://github.com/robtaussig/react-use-websocket/issues/242
let useWebSocket = ReactUseWebSocket;
while (typeof useWebSocket !== 'function' && useWebSocket?.default) {
  useWebSocket = useWebSocket.default;
}
const { ReadyState } = ReactUseWebSocket;

const header = {
  host: process.env.NEXT_PUBLIC_WS_HOST,
  Authorization: 'x',
};
const payload = {};
const encodedHeader = btoa(JSON.stringify(header));
const encodedPayload = btoa(JSON.stringify(payload));

const url = new URL(process.env.NEXT_PUBLIC_WS_URL || 'https://example.com');
url.search = new URLSearchParams({
  header: encodedHeader,
  payload: encodedPayload,
});
const FULL_URL = url.toString();

// For dev purposes, you can remove all parameters and it will return all
// subscription onNewMessageReceipt {
//   onNewMessageReceipt {
const SUBSCRIPTION_QUERY_NATIONAL = `
  subscription onNewMessageReceipt {
    onNewMessageReceipt(isPetitionSign: false) {
      ... on MessageReceipt {
        correlationId
        created
        content {
          ... on UserMessage {
            message
            subject
          }
        }
      }
      sender {
        city
        state
        verifiedVoter
      }
    }
  }
`;
const SUBSCRIPTION_QUERY_STATE = `
  subscription onNewMessageReceipt ($stateCode: String!) {
    onNewMessageReceipt(isPetitionSign: false, state: $stateCode) {
      ... on MessageReceipt {
        correlationId
        created
        content {
          ... on UserMessage {
            message
            subject
          }
        }
      }
      sender {
        city
        state
        verifiedVoter
      }
    }
  }
`;

const LettersPageCrumbs = ({ stateCode }) => {
  return (
    stateCode && (
      <ol
        aria-label="breadcrumbs"
        className="flex gap-2 text-sm tracking-wider uppercase"
      >
        <li className="after:content-['→']">
          <Anchor
            className="mr-2 anchor-subtle"
            href={`/${DATA_TYPES.LETTER.basePath}`}
          >
            United States
          </Anchor>
        </li>
        <li>{getStateProperName(stateCode)}</li>
      </ol>
    )
  );
};

const LettersPage = ({
  openLetters = [],
  moreLettersCallback = () => {},
  isLoadingMore = false,
  stateTotals = [],
  stateCode = null,
  hasMore = false,
}) => {
  // Used to track which ones we have, so that if a WS one comes in (already have, dupe, etc) we don't show it
  const [seenIds, setSeenIds] = useState(
    openLetters.map((l) => l.correlationId)
  );
  const [newLettersCount, setNewLettersCount] = useState(0);
  const [executeScroll, scrollPoint] = useScroll();
  const [forwardLetters, setForwardLetters] = useState([]);

  const intersectionRef = useRef(null);
  const isIntersecting = useIntersection(intersectionRef, {});

  // Hanle clicking the new letters pill
  const handleAction = () => {
    executeScroll();
  };

  // Initialize the socket
  const { sendMessage, lastJsonMessage, readyState } = useWebSocket(FULL_URL, {
    protocols: ['graphql-ws'],
    // onClose: (e) => console.log('Closing Connection', e),
    // onOpen: (e) => console.log('Opening Connection', e),
    // onError: (e) => console.log('Error', e),
    onError: (e) => {
      Sentry.captureException(e);
    },
  });

  // Set up the subscription
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendMessage(
        JSON.stringify({
          id: crypto.randomUUID(),
          type: 'start',
          payload: {
            data: JSON.stringify({
              query: stateCode
                ? SUBSCRIPTION_QUERY_STATE
                : SUBSCRIPTION_QUERY_NATIONAL,
              variables: stateCode ? { stateCode } : {},
            }),
            extensions: {
              authorization: {
                Authorization: 'x',
                host: process.env.NEXT_PUBLIC_WS_HOST,
              },
            },
          },
        })
      );
    }
  }, [readyState, sendMessage, stateCode]);

  // Handle a new message
  useEffect(() => {
    const letterData = lastJsonMessage?.payload?.data?.onNewMessageReceipt;
    if (
      letterData &&
      lastJsonMessage.type === 'data' &&
      !seenIds.includes(letterData.correlationId)
    ) {
      const formattedLetter = themisMessageReceiptEventToLetter(letterData);

      // Only increment the count if we're not at the top of the page
      if (!isIntersecting) {
        setNewLettersCount((prev) => prev + 1);
      }
      setForwardLetters((prev) =>
        [formattedLetter, ...prev].sort(
          (a, b) => b.sentTimestamp - a.sentTimestamp
        )
      );
      setSeenIds((prev) => [formattedLetter.correlationId, ...prev]);
    }
  }, [lastJsonMessage, seenIds, isIntersecting]);

  const hasLettersToDisplay =
    openLetters.length > 0 || forwardLetters.length > 0;

  return (
    <>
      {/* Breadcrumbs */}
      <LettersPageCrumbs stateCode={stateCode} />

      {/* Page Header */}
      <div ref={intersectionRef}>
        <section ref={scrollPoint}>
          <h1 className="mb-2 md:mb-0">
            {stateCode ? getStateProperName(stateCode) : null} Open Letters
          </h1>
          <p className="mb-2 hidden md:block" size="sm">
            Unique letters delivered to elected officials from constituents &
            verified voters.{' '}
            <Anchor
              className="anchor-text"
              href={`/${DATA_TYPES.PETITION.basePath}`}
            >
              See petitions
            </Anchor>{' '}
            for organized&nbsp;campaigns.
          </p>
        </section>
      </div>

      {/* Hex Map */}
      <HexMap
        states={stateTotals.states}
        highestStateCount={stateTotals.highestTotal}
        basePath={DATA_TYPES.LETTER.basePath}
      />

      {/* Letters */}
      {!hasLettersToDisplay ? (
        <EmptyResults type={DATA_TYPES.LETTER} />
      ) : (
        <div className="flex flex-col gap-1">
          {!isIntersecting ? (
            <MoreLettersPill
              newLettersCount={newLettersCount}
              handleAction={handleAction}
            />
          ) : null}
          {openLetters.map((l) => (
            <Letter
              key={l.correlationId}
              letter={l}
              shouldFade={l.shouldFade}
            />
          ))}
          {isLoadingMore && (
            <>
              <LoadingLetter />
              <LoadingLetter />
            </>
          )}
        </div>
      )}

      {/* Request more, mastodon buttons */}
      {hasMore ? (
        <Button
          theme="light"
          onClick={moreLettersCallback}
          className="mt-1 w-full"
          preventDefault
          disabled={isLoadingMore}
        >
          {isLoadingMore ? 'Loading...' : 'Load More'}
        </Button>
      ) : (
        <Button
          iconSrc={mastodon}
          theme="light"
          href={config.socialUrls.openletters}
          className="mt-1 w-full"
        >
          See More on Mastodon
        </Button>
      )}

      <div className="m-10 flex flex-col gap-2 rounded-sm bg-transparent text-center">
        <p>
          Write your own letter and we’ll deliver instantly by email, fax, or
          postal mail to the officials you want to reach. It takes two minutes
          to be&nbsp;heard.
        </p>
        <CTA className="mx-auto" keyword="write" copy="Write a Letter" />
      </div>
    </>
  );
};

LettersPage.propTypes = {
  openLetters: PropTypes.arrayOf(OpenLetterTypeList),
  stateTotals: PropTypes.shape({
    states: PropTypes.shape({
      [PropTypes.string]: PropTypes.number,
    }),
    highestTotal: PropTypes.number,
  }),
  stateCode: PropTypes.string,
  moreLettersCallback: PropTypes.func,
  isLoadingMore: PropTypes.bool,
  hasMore: PropTypes.bool,
};

export default LettersPage;
